import { useCallback, useState } from 'react'

import {
  BankingEntitiesBankAccountNumberFormatEnum,
  FixContractsContractVersionDocument,
  SetBankingInformationDocument,
  UpdateEmployeeDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'

/**
 * Writes form values for the `data_input` StepAction directly through the
 * production mutations, avoiding the data-dispatcher schema-mapper path which
 * currently has permission issues for this workflow:
 *
 *  - `UpdateEmployee` → personal-info fields except bank. Gated by
 *    `Employees::EmployeeEditPersonalInfoPolicy` (verb `EditPersonalInfo`,
 *    self-grantable owned_only).
 *  - `SetBankingInformation` → bank fields (account number, format, BIC).
 *    Bank data lives on the `Banking::BankAccountNumber` entity, which is
 *    written through this dedicated mutation; `UpdateEmployee` doesn't accept
 *    `bankNumberFormat` at all, and the standard profile editor's BankInfo
 *    block uses this same mutation.
 *  - `FixContractsContractVersion` → contract fields. Gated by
 *    `ContractPolicy::EditContractVersion` (verb `EditContracts`, HR-only by
 *    design — matches processes' `<Assignees excludeAffectedUser />`).
 *
 * The three calls run sequentially in employee → banking → contract order, and
 * short-circuit on the first failure. Partial success (employee written then
 * banking fails) is accepted for v1 — there is no cross-aggregate transaction
 * across these three backend components.
 */

export type DataInputFieldValue = string | number | boolean

export interface DataInputSubmitInput {
  employeeId: string
  accessId: string
  contractVersionId: string | null
  // camelCase keys, e.g. firstName, workingWeekDays, salaryAmount. Absence is
  // expressed by omitting the key; explicit "clear this field" is not modeled
  // because the underlying mutations don't accept nulls without a separate
  // schema decision.
  values: Record<string, DataInputFieldValue>
}

export interface DataInputFieldError {
  field: string | null
  messages: string[]
}

export type DataInputSubmitResult =
  | { success: true }
  | { success: false; fieldErrors: DataInputFieldError[]; message?: string }

const EMPLOYEE_FIELDS = [
  'firstName',
  'lastName',
  'preferredName',
  'birthdayOn',
  'gender',
  'nationality',
  'phoneNumber',
  'personalEmail',
  'contactName',
  'contactNumber',
  'addressLine1',
  'addressLine2',
  'city',
  'postalCode',
  'state',
  'country',
  'identifierType',
  'identifier',
  'socialSecurityNumber',
] as const

const BANK_FIELDS = ['bankNumber', 'bankNumberFormat', 'swiftBic'] as const

const CONTRACT_FIELDS = [
  'effectiveOn',
  'startsOn',
  'jobCatalogTreeNodeUuid',
  'jobCatalogLevelId',
  'jobTitle',
  'esJobDescription',
  'salaryAmount',
  'salaryFrequency',
  'workingHours',
  'workingHoursFrequency',
  'workingWeekDays',
  'maxLegalYearlyHours',
  'maximumWeeklyHours',
] as const

// Fields ending in a numeric suffix don't round-trip cleanly through the
// GraphQL camelCase convention — `address_line_1` would camelCase to
// `addressLine1`, but the controller's snake_case re-conversion produces
// `address_line1` (no underscore before the digit), which then doesn't match
// the permitted `address_line_1` column. The backend works around this by
// accepting dedicated `gql_address_line1` / `gql_address_line2` variants
// (see `Employees::Dtos::Employee::Update`), so we route those two fields
// through the `gql*` variables to land them on the right column.
const EMPLOYEE_FIELD_VARIABLE_OVERRIDES: Readonly<Record<string, string>> = {
  addressLine1: 'gqlAddressLine1',
  addressLine2: 'gqlAddressLine2',
}
const INTEGER_CONTRACT_FIELDS = new Set<string>([
  'workingHours',
  'maxLegalYearlyHours',
  'maximumWeeklyHours',
])
const ID_CONTRACT_FIELDS = new Set<string>(['jobCatalogLevelId'])

const hasOwn = (object: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(object, key)

const pickFields = <K extends string>(
  values: Record<string, DataInputFieldValue>,
  fields: readonly K[]
): Partial<Record<K, DataInputFieldValue>> => {
  const out: Partial<Record<K, DataInputFieldValue>> = {}
  for (const key of fields) {
    if (hasOwn(values, key)) {
      const value = values[key]
      if (value !== undefined) {
        out[key] = value
      }
    }
  }
  return out
}

const buildEmployeeVariables = (
  values: Record<string, DataInputFieldValue>
): Record<string, DataInputFieldValue> => {
  const picked = pickFields(values, EMPLOYEE_FIELDS)
  const out: Record<string, DataInputFieldValue> = {}
  for (const key of EMPLOYEE_FIELDS) {
    const value = picked[key]
    if (value !== undefined) {
      const variable = EMPLOYEE_FIELD_VARIABLE_OVERRIDES[key] ?? key
      out[variable] = value
    }
  }
  return out
}

// The dynamic_forms schema constrains the form-side `bankNumberFormat` value
// to one of the BankingEntitiesBankAccountNumberFormatEnum values via the
// `allowedValues` selector, but TS only sees the raw `string`. Narrow at
// build time so the typed mutation variable lands correctly.
const BANK_ACCOUNT_NUMBER_FORMATS = new Set<string>([
  'bank_name_and_account_number',
  'clabe',
  'iban',
  'other',
  'routing_number_and_account_number',
  'sort_code_and_account_number',
])

const isBankAccountNumberFormat = (
  value: string
): value is BankingEntitiesBankAccountNumberFormatEnum => BANK_ACCOUNT_NUMBER_FORMATS.has(value)

interface BankingVariables {
  accountNumber: string
  format: BankingEntitiesBankAccountNumberFormatEnum
  complementaryData?: string
}

interface BankingPlan {
  // SetBankingInformation requires BOTH accountNumber and format (the format
  // field is a non-null enum on the mutation). When the form has any bank
  // field present, surface either a clean banking call or a pre-flight error
  // pointing at the missing input so the user sees what's needed.
  variables: BankingVariables | null
  fieldErrors: DataInputFieldError[]
}

const buildBankingPlan = (values: Record<string, DataInputFieldValue>): BankingPlan => {
  const picked = pickFields(values, BANK_FIELDS)
  const hasAny =
    picked.bankNumber !== undefined ||
    picked.bankNumberFormat !== undefined ||
    picked.swiftBic !== undefined
  if (!hasAny) {
    return { variables: null, fieldErrors: [] }
  }

  const bankNumber = typeof picked.bankNumber === 'string' ? picked.bankNumber.trim() : ''
  const format = typeof picked.bankNumberFormat === 'string' ? picked.bankNumberFormat.trim() : ''
  const complementary =
    typeof picked.swiftBic === 'string' && picked.swiftBic.trim() !== ''
      ? picked.swiftBic.trim()
      : undefined

  const errors: DataInputFieldError[] = []
  if (bankNumber === '') {
    errors.push({ field: 'bank_number', messages: ['cannot be blank when updating bank details'] })
  }
  if (format === '') {
    errors.push({
      field: 'bank_number_format',
      messages: ['must be selected when updating bank details'],
    })
  } else if (!isBankAccountNumberFormat(format)) {
    errors.push({
      field: 'bank_number_format',
      messages: [`is not a valid bank account number format`],
    })
  }
  if (errors.length > 0) {
    return { variables: null, fieldErrors: errors }
  }

  // Narrowed by the isBankAccountNumberFormat guard above; assert again here
  // for TS since the narrowing doesn't carry across the early-return branch.
  if (!isBankAccountNumberFormat(format)) {
    return { variables: null, fieldErrors: [] }
  }
  return {
    variables: { accountNumber: bankNumber, format, complementaryData: complementary },
    fieldErrors: [],
  }
}

const coerceNumericString = (value: DataInputFieldValue): DataInputFieldValue => {
  if (typeof value === 'number') return value
  if (typeof value === 'string' && value.trim() !== '') {
    const numeric = Number(value)
    return Number.isFinite(numeric) ? numeric : value
  }
  return value
}

const coerceContractValue = (key: string, value: DataInputFieldValue): DataInputFieldValue => {
  if (key === 'salaryAmount') {
    // The mutation's salaryAmount is BigInt (typed `any` in generated code).
    // FixFlow passes the value through as a Number (see
    // contracts/components/FixFlow/lib/fixFlowForm.ts), so we mirror that.
    return coerceNumericString(value)
  }
  if (INTEGER_CONTRACT_FIELDS.has(key)) {
    return coerceNumericString(value)
  }
  if (ID_CONTRACT_FIELDS.has(key)) {
    return typeof value === 'string' ? value : String(value)
  }
  // Dates and string fields pass through; the form supplies ISO8601Date
  // (`YYYY-MM-DD`) strings directly.
  return value
}

const buildContractVariables = (
  values: Record<string, DataInputFieldValue>
): Record<string, DataInputFieldValue> => {
  const picked = pickFields(values, CONTRACT_FIELDS)
  const out: Record<string, DataInputFieldValue> = {}
  for (const key of CONTRACT_FIELDS) {
    const value = picked[key]
    if (value !== undefined) {
      out[key] = coerceContractValue(key, value)
    }
  }
  return out
}

// Errors come back from the mutations as a union of `SimpleError` and
// `StructuredError`. Narrow by property presence rather than `as` so the
// linter stays happy.
type SimpleErrorLike = { __typename?: 'SimpleError'; message: string }
type StructuredErrorLike = { __typename?: 'StructuredError'; field: string; messages: string[] }

const isStructuredError = (error: unknown): error is StructuredErrorLike => {
  if (typeof error !== 'object' || error === null) return false
  if (!('field' in error) || !('messages' in error)) return false
  return typeof error.field === 'string' && Array.isArray(error.messages)
}

const isSimpleError = (error: unknown): error is SimpleErrorLike => {
  if (typeof error !== 'object' || error === null) return false
  if (!('message' in error)) return false
  return typeof error.message === 'string'
}

const normalizeErrors = (
  rawErrors: readonly unknown[] | undefined | null
): { fieldErrors: DataInputFieldError[]; message?: string } => {
  if (!rawErrors || rawErrors.length === 0) {
    return { fieldErrors: [] }
  }

  const fieldErrors: DataInputFieldError[] = []
  let topLevelMessage: string | undefined

  for (const error of rawErrors) {
    if (isStructuredError(error)) {
      const messages = error.messages.filter(
        (message): message is string => typeof message === 'string'
      )
      if (messages.length > 0) {
        fieldErrors.push({ field: error.field, messages })
      }
      continue
    }
    if (isSimpleError(error)) {
      // Unscoped SimpleError — surface as the top-level message; keep the
      // first one we see, and also push it as an unfielded entry so the
      // caller can iterate uniformly.
      if (!topLevelMessage) topLevelMessage = error.message
      fieldErrors.push({ field: null, messages: [error.message] })
    }
  }

  return topLevelMessage ? { fieldErrors, message: topLevelMessage } : { fieldErrors }
}

export const useDataInputUpdate = (): {
  submit: (input: DataInputSubmitInput) => Promise<DataInputSubmitResult>
  loading: boolean
} => {
  // The core `useMutation` wrapper requires an `extractorFn` that pulls the
  // mutation payload (the object holding `errors`) out of the response data,
  // so the shared `handleResult` helper can normalize success / failure into
  // a `Result<TData>` shape. We pass `() => {}` to `handleResult` (and use
  // `silent: true`) because this hook does its own per-mutation field-error
  // mapping via `normalizeErrors`/`guessFormKeyForMutationField` in the
  // renderer — we don't want the default flash + form-callback behavior.
  const [updateEmployee, { loading: employeeLoading }] = useMutation(UpdateEmployeeDocument, {
    extractorFn: (data) => data.employeesMutations.updateEmployeesEmployee,
  })
  const [setBankingInformation, { loading: bankingLoading }] = useMutation(
    SetBankingInformationDocument,
    {
      extractorFn: (data) => data.bankingMutations.upsertBankingBankAccountNumber,
    }
  )
  const [fixContractVersion, { loading: contractLoading }] = useMutation(
    FixContractsContractVersionDocument,
    {
      extractorFn: (data) => data.contractsMutations.fixContractsContractVersion,
    }
  )
  const [coordinatorBusy, setCoordinatorBusy] = useState(false)

  const submit = useCallback(
    async (input: DataInputSubmitInput): Promise<DataInputSubmitResult> => {
      const { employeeId, accessId, contractVersionId, values } = input
      const employeeVars = buildEmployeeVariables(values)
      const bankingPlan = buildBankingPlan(values)
      const contractVars = buildContractVariables(values)

      const hasEmployeeWork = Object.keys(employeeVars).length > 0
      const hasBankingWork = bankingPlan.variables !== null
      const hasContractWork = contractVersionId !== null && Object.keys(contractVars).length > 0

      // Pre-flight: if the user touched bank fields but didn't satisfy the
      // mutation's non-null requirements, surface the gap before we attempt
      // any write so the form lands the inline errors on the right inputs.
      if (bankingPlan.fieldErrors.length > 0) {
        return { success: false, fieldErrors: bankingPlan.fieldErrors }
      }

      if (!hasEmployeeWork && !hasBankingWork && !hasContractWork) {
        return { success: true }
      }

      setCoordinatorBusy(true)
      try {
        // Sequential: employee → banking → contract. Short-circuit on the
        // first failure; partial success across the three components is
        // accepted (no cross-aggregate rollback available server-side).
        if (hasEmployeeWork) {
          const unhandled = await updateEmployee({
            variables: { id: employeeId, accessId, ...employeeVars },
          })
          const result = handleResult(unhandled, () => {}, { silent: true })
          if (!result.success) {
            const normalized = normalizeErrors(result.errors)
            if (normalized.fieldErrors.length > 0) {
              return { success: false, ...normalized }
            }
          }
        }

        if (hasBankingWork && bankingPlan.variables !== null) {
          const unhandled = await setBankingInformation({
            variables: { employeeId, ...bankingPlan.variables },
          })
          const result = handleResult(unhandled, () => {}, { silent: true })
          if (!result.success) {
            const normalized = normalizeErrors(result.errors)
            if (normalized.fieldErrors.length > 0) {
              return { success: false, ...normalized }
            }
          }
        }

        if (hasContractWork && contractVersionId !== null) {
          const unhandled = await fixContractVersion({
            variables: {
              id: contractVersionId,
              employeeId,
              customFields: [],
              ...contractVars,
            },
          })
          const result = handleResult(unhandled, () => {}, { silent: true })
          if (!result.success) {
            const normalized = normalizeErrors(result.errors)
            if (normalized.fieldErrors.length > 0) {
              return { success: false, ...normalized }
            }
          }
        }

        return { success: true }
      } finally {
        setCoordinatorBusy(false)
      }
    },
    [updateEmployee, setBankingInformation, fixContractVersion]
  )

  return {
    submit,
    loading: coordinatorBusy || employeeLoading || bankingLoading || contractLoading,
  }
}
